<script setup lang="ts">
import { QuillEditor } from '@vueup/vue-quill'
import { ref, watch } from 'vue'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { useField, useForm } from 'vee-validate'
import { useRoute, useRouter } from 'vue-router'
import * as yup from 'yup'
import { getPostAction, updatePostAction } from '@/api/functions/settings/blogs-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const slug = route.params.id as string

const { data: postData, refetch } = useQueryApi({
  queryKey: ['post-details', slug],
  queryFn: () => getPostAction(slug),
  staleTime: 1000 * 60 * 2,
})

const excerptError = ref(false)

const schema = yup.object({
  title: yup.string().required('عنوان مطلب الزامی است'),
  excerpt: yup.string().required('محتوای مطلب الزامی است'),
  imageFile: yup.mixed().nullable(),
})

const { handleSubmit, errors, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    title: '',
    excerpt: '',
    imageFile: null,
  },
})

const { value: title } = useField<string>('title')
const { value: excerpt } = useField<string>('excerpt')
const { value: imageFile } = useField<File | null>('imageFile')

const img_Url = ref('')
const fileInput = ref<HTMLInputElement | null>(null)

watch(
  () => postData.value,
  (newData) => {
    if (newData) {
      const post = newData.data
      setValues({
        title: post.title || '',
        excerpt: post.content || '',
        imageFile: null,
      })

      img_Url.value = post.thumbnail || ''
    }
  },
  { immediate: true }
)

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  [{ header: 1 }, { header: 2 }], // custom button values
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
  [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
  [{ direction: 'rtl' }], // text direction
  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  ['clean'], // remove formatting button
]

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    img_Url.value = URL.createObjectURL(file)
    imageFile.value = file
  }
}

const triggerFileUpload = () => fileInput.value?.click()

const updateMutation = useMutationApi({
  mutationFn: () =>
    updatePostAction(slug, {
      title: title.value,
      content: excerpt.value,
      image: imageFile.value ?? undefined,
    }),

  onSuccess: () => {
    toast.success('مطلب با موفقیت بروزرسانی شد')
    router.push({
      path: `/settings/blog/${slug}/edit`,
    })
    refetch()
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err))
  },
})

const onSubmit = handleSubmit(() => {
  if (excerpt.value === '<p><br></p>') {
    excerptError.value = true
  } else {
    excerptError.value = false
    updateMutation.mutate()
  }
})
</script>

<template>
    <div class="mb-9">
        <router-link to="/settings/blog"
            class="w-fit mb-6 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
            <img src="/svg/back-icon.svg" class="me-1" width="22" />
            <span>بازگشت</span>
        </router-link>

        <div class=" flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-0 mb-9">
            <div class="text-[#1b325a] text-[22px]">
                جزئیات مطلب: {{ postData?.data?.thumbnail }}
            </div>

            <!-- actions  -->
            <div class="w-full lg:w-auto flex flex-row items-center gap-2">
                <Button :isAdmin="true" :loading="updateMutation.isPending.value"
                    :disabled="updateMutation.isPending.value" @click="onSubmit"
                    class=" w-1/2 lg:w-auto px-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
                    <span class="text-nowrap flex items-center  justify-center gap-2 ">
                        بروزرسانی
                    </span>
                </Button>
            </div>
        </div>

        <div
            class="w-full flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-24 lg:h-28 p-[26px] bg-white rounded-xl">
            <span class="text-[#1b325a] text-sm text-nowrap">عنوان مطلب</span>
            <Input v-model="title" name="title" type="text" class="h-[50px]!" :error="errors.title" />
        </div>

        <div
            class="w-full flex flex-col lg:flex-row  lg:items-center gap-6 lg:gap-24  p-[26px] bg-white rounded-xl my-4">
            <span class="text-[#1b325a] text-sm">تصویر نمایه</span>

            <div class="flex flex-col gap-4">
                <div v-if="!img_Url" class="w-full lg:w-[360px] h-[211px] rounded-xl bg-[#f5f5f5] object-cover">
                </div>

                <img v-else :src="img_Url" class="w-full lg:w-[360px] h-[211px] rounded-xl border object-cover" />

                <Button @click="triggerFileUpload" :isAdmin="true" variant="outline"
                    class="w-fit px-3.5! border-2 rounded-lg! h-8! py-0 text-[13px] font-normal">
                    {{ !img_Url ? 'بارگذاری' : 'بارگذاری دوباره' }}
                </Button>

                <input type="file" accept="image/*" ref="fileInput" class="hidden" @change="handleFileChange" />
            </div>
        </div>

        <QuillEditor v-model:content="excerpt" theme="snow" :toolbar="toolbarOptions"
            class="bg-white border-0! min-h-[340px] rounded-b-xl text-right" placeholder="شروع به نوشتن کنید"
            content-type="html" />

        <div v-if="excerptError" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
            <img :src="ErrorIcon" width="16" alt="error-icon" />
            این فیلد ضروری است
        </div>

    </div>
</template>

<style>
@media (min-width: 1025px) {
    .ql-toolbar.ql-snow {
        height: 46px;
    }
}

.ql-toolbar.ql-snow {
    background-color: white;
    border-color: #eaeef5;
    border-top: none;
    border-inline: none;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
}

.ql-toolbar {
    direction: ltr !important;
    text-align: left !important;
}

.ql-editor {
    direction: rtl;
    text-align: right;
    font-family: inherit;
}
</style>
